import {
    domainColumns,
    formatOutOf,
    formatYesNo,
    getNumber,
    getScale,
    getScales,
    getString,
    mapScreeningLabel,
    type OutcomeTableConfig,
} from '../helpers';

export const aq10Config: OutcomeTableConfig = {
    title: 'Resultados AQ-10',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');

        return [
            {
                domain: 'Traços do espectro autista (total AQ-10)',
                result: formatOutOf(total.raw, 10),
                interpretation: getString(total.interpretation_label),
            },
            {
                domain: 'Encaminhamento indicado (ponto de corte >= 6)',
                result: formatYesNo(total.screening_positive),
                interpretation: mapScreeningLabel(total.screening_positive),
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');

        // AQ-10's shape is a subset of HCL-32/M-CHAT-R/QFC totals; the
        // negative guards keep those payloads from resolving here.
        return (
            'screening_positive' in total &&
            'interpretation_label' in total &&
            Object.keys(scales).length === 1 &&
            !('angst_cutoff_positive' in total) &&
            !('follow_up_interview_indicated' in total) &&
            !('above_reference_mean' in total)
        );
    },
};

export const mchatrConfig: OutcomeTableConfig = {
    title: 'Resultados M-CHAT-R',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');

        return [
            {
                domain: 'Risco de TEA (total M-CHAT-R)',
                result: formatOutOf(total.raw, 20),
                interpretation: getString(total.interpretation_label),
            },
            {
                domain: 'Rastreio positivo (ponto de corte >= 3)',
                result: formatYesNo(total.screening_positive),
                interpretation: mapScreeningLabel(total.screening_positive),
            },
            {
                domain: 'Entrevista de seguimento (M-CHAT-R/F)',
                result: formatYesNo(total.follow_up_interview_indicated),
                interpretation: 'Segunda etapa recomendada para escores 3-7',
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        return 'follow_up_interview_indicated' in getScale(scales, 'total');
    },
};

export const mdqConfig: OutcomeTableConfig = {
    title: 'Resultados MDQ',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');
        const symptoms = getScale(scales, 'symptoms');
        const coOccurrence = getScale(scales, 'co_occurrence');
        const impairment = getScale(scales, 'impairment');

        return [
            {
                domain: 'Rastreio de espectro bipolar (total MDQ)',
                result: formatOutOf(total.raw, 13),
                interpretation: getString(total.interpretation_label),
            },
            {
                domain: 'Critério 1 - Sintomas (>= 7 de 13)',
                result: formatOutOf(symptoms.raw, 13),
                interpretation: getString(symptoms.interpretation_label),
            },
            {
                domain: 'Critério 2 - Co-ocorrência (item 14)',
                result: formatYesNo(coOccurrence.criterion_met),
                interpretation: getString(coOccurrence.interpretation_label),
            },
            {
                domain: 'Critério 3 - Prejuízo funcional (item 15)',
                result: getNumber(impairment.raw),
                interpretation: getString(impairment.interpretation_label),
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        return (
            'criterion_met' in getScale(scales, 'co_occurrence') &&
            'criterion_met' in getScale(scales, 'impairment')
        );
    },
};

export const hcl32Config: OutcomeTableConfig = {
    title: 'Resultados HCL-32',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');

        return [
            {
                domain: 'Sintomas de hipomania (total HCL-32)',
                result: formatOutOf(total.raw, 32),
                interpretation: getString(total.interpretation_label),
            },
            {
                domain: 'Corte brasileiro (ponto de corte >= 18)',
                result: formatYesNo(total.screening_positive),
                interpretation: mapScreeningLabel(total.screening_positive),
            },
            {
                domain: 'Corte internacional de Angst (>= 14)',
                result: formatYesNo(total.angst_cutoff_positive),
                interpretation: mapScreeningLabel(total.angst_cutoff_positive),
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        return 'angst_cutoff_positive' in getScale(scales, 'total');
    },
};

export const qfcConfig: OutcomeTableConfig = {
    title: 'Resultados QFC',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');

        return [
            {
                domain: 'Falhas cognitivas (total QFC)',
                result: formatOutOf(total.raw, 100),
                interpretation: getString(total.interpretation_label),
            },
            {
                domain: 'Acima da média de referência brasileira (35,58)',
                result: formatYesNo(total.above_reference_mean),
                interpretation: 'Comparação com a média da amostra normativa brasileira',
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        return 'above_reference_mean' in getScale(scales, 'total');
    },
};
